import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import {UserSubscription} from "../../../../../user/user-subscription";
import {User} from "../../../../../user/user";
import {FormControl, Validators} from "@angular/forms";
import {DbValidators} from "../../../../../shared/validators/DbValidators";
import {distinctUntilChanged, filter, first, switchMap, takeUntil} from "rxjs/operators";
import {BoosterMode, BoosterService} from "../../services/booster.service";
import {
  RemoveBoostedPageDialogComponent
} from "../../dialogs/remove-boosted-page-dialog/remove-boosted-page-dialog.component";
import {DbModalService} from "../../../../../modules/modal/modal";
import {NotifierComponent} from "../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../shared/components/notifier/notification-type";
import {TextUtils} from "../../../../../shared/utils/text-utils";
import {Subject} from "rxjs";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../../store";
import {BoosterPage} from "../../mappers/booster-page";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";
import {TypeUtils} from "../../../../../shared/utils/type-utils";
import {BoosterPageMapper} from "../../mappers/booster-page-mapper";
import {Utils} from "../../../../../shared/utils/utils";
import {ActivatedRoute, Router} from "@angular/router";
import {combineLatest} from "rxjs";
import {Website} from "../../../website";
import features from "./custom-rules-features.json";
import {booster} from "../../../../../website-sidebar/website-sidebar-sub-menu";
import {BuilderSectionsInteractionService} from "../../../../../builder-v2/builder-sections-interaction.service";

@Component({
    selector: 'db-custom-rules',
    templateUrl: './custom-rules.component.html',
    styleUrls: ['./custom-rules.component.scss'],
    host: { '[class.free-plan]': 'user.starterClient' },
    standalone: false
})
export class CustomRulesComponent implements OnInit, OnDestroy {
  protected readonly booster = booster;
  public currentSubscription: UserSubscription;
  public user: User;
  public boosterMode: BoosterMode;
  public website: Website;
  public testModeEnabled: boolean;
  public loading: boolean;
  public addedNewPage: boolean = false;
  public notOptimizedPagesCount: any;
  public modeChangesMode: BoosterMode;
  public pageUrl: FormControl = new FormControl('');
  public optimizedPages: BoosterPage[] | null = null;
  public optimizedPagesLoading: boolean = true;
  public serverError: string = null;
  public notConnected: boolean;
  public showPercentOffer: boolean;
  public modeChanges: boolean;
  public modeChangesIndex: number;
  public demoUser: boolean = false;
  public boosterPluginIsActive: boolean = false;
  public boosterPluginIsOld: boolean;
  public features: string[] = features;
  public boosterUpdateAvailable: boolean;
  companyName: string;

  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _boosterService: BoosterService,
    private _dbModalService: DbModalService,
    private _cdRef: ChangeDetectorRef,
    private _store: Store<fromStore.DbStoreState>,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _builderSectionsInteractionService: BuilderSectionsInteractionService,
  ) {
  }

  get showUpgrade(): boolean {
    if (this.currentSubscription && this.website && this.currentSubscription.plan.services && this.currentSubscription.plan.services.speed_optimizer) {
      return (!(this.currentSubscription.paidClient || !!this.website.domain_hash) &&
        this.optimizedPages && this.optimizedPages.length >= (this.currentSubscription.plan.services.speed_optimizer.speed_optimizier_optimizable_pages_max_count.parameter_value));
    }
    return false;
  }

  ngOnInit() {
      this._getCompanyInfo();
    this.website = this._route.snapshot.parent.parent.data['website'];
    if (!TypeUtils.isNull(this._route.snapshot.data['boosterTroubleshootData'])) {
      const disconnected = this._route.snapshot.data['boosterTroubleshootData'] === 'alive_but_not_connected';
      const restNoRoute = this._route.snapshot.data['boosterTroubleshootData'] === 'error';
      this.notConnected = disconnected || restNoRoute;
    }
    this._getUser();
    this._setValidators();
    this._checkBoosterState();
    this._getBoosterData();
    this._getModeLevel();
    this._getTestMode();
    this._showPercentOffer();
    this._checkBoosterPluginUpdateAvailable();
  }

  private _checkBoosterState(): void {
    this._store.select(fromStore.getWebsiteOptimizerPlugin(this.website?.id))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe((optimizerPlugin): void => {
        this.boosterPluginIsOld = TypeUtils.isEmpty(optimizerPlugin);
        if (optimizerPlugin && !TypeUtils.isEmpty(optimizerPlugin)) {
          this.boosterPluginIsActive = optimizerPlugin.active;
        }
      });
  }

  private _showPercentOffer() {
    const percentOfferEndDate = new Date("2024-01-30 18:00:00").getTime();
    const datesDiff = Utils.getTimeDiffFromToday(percentOfferEndDate);
    this.showPercentOffer = datesDiff && this.currentSubscription && this.currentSubscription.trialBoosterClient;
  }

  private _getUser(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserSubscription)
        ))
      )
      .subscribe(([user, userSubscription]) => {
        this.user = user;
        this.currentSubscription = userSubscription;
        this.demoUser = Utils.isDemoUser(this.currentSubscription, user);
      });
  }

  private _getBoosterData(): void {
    this._store.select(fromStore.getWebsiteBoosterDataLoaded(this.website?.id))
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getWebsiteBoosterPages(this.website?.id)),
          this._store.select(fromStore.getWebsiteBoosterCustomPages(this.website?.id))
        )),
        distinctUntilChanged(([prevPages, prevCustomPages], [currPages, currCustomPages]) => {
          return prevPages && currPages && prevCustomPages && currCustomPages
            &&
            (
              (!this.modeChanges && JSON.stringify(prevPages) === JSON.stringify(currPages)
                && JSON.stringify(prevCustomPages) === JSON.stringify(currCustomPages))
              ||
              (this.modeChanges && prevPages[this.modeChangesIndex]?.mode === currPages[this.modeChangesIndex]?.mode
                && prevCustomPages[this.modeChangesIndex]?.mode === currCustomPages[this.modeChangesIndex]?.mode)
            );
        }),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([pages, customPages]) => {
        const pagesData = this.currentSubscription.paidClient ? customPages : pages;
        !TypeUtils.isNull(pagesData) && this._getPagesData(pagesData);
      });
  }

  private _getModeLevel(): void {
    this._store.select(fromStore.getWebsiteBoosterModeLevel(this.website?.id))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(modeLevel => {
        if (!TypeUtils.isNull(modeLevel)) {
          this.boosterMode = modeLevel;
        }
      });
  }

  private _getTestMode(): void {
    this._store.select(fromStore.getWebsiteBoosterTestMode(this.website?.id))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(testMode => {
        if (!TypeUtils.isNull(testMode)) {
          this.testModeEnabled = !TypeUtils.isNull(testMode) && testMode === 'on';
        }
      });
  }

  private _getPagesCount(): void {
    this._store.select(fromStore.getWebsiteBoosterPagesCount(this.website?.id))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(pagesCount => {
        if (!TypeUtils.isNull(pagesCount)) {
          this.notOptimizedPagesCount = this.optimizedPages && pagesCount > this.optimizedPages.length ? pagesCount - this.optimizedPages.length : 0;
        }
      });
  }

  private _getPagesData(pages): void {
    if (!this.addedNewPage) {
      this.optimizedPages = pages;
      const hasNotOptimizedPage = this.optimizedPages.find(page => page && page.mode && !page.date && page.status !== 'not_started' && page.mode !== 'no_optimize' && page.status !== 'error');
      if (hasNotOptimizedPage) {
        this.optimizedPagesLoading = true;
        /* wait 2 minutes and dispatch booster data if there is a not optimized page */
        setTimeout(() => {
          this._store.dispatch(new fromStore.LoadBoosterData({websiteId: this.website?.id}));
        }, 2 * 60 * 60 * 1000);
      } else {
        this.optimizedPagesLoading = false;
      }
      this._getPagesCount();
    }
    this.addedNewPage = false;
  }

  public submitted($event): void {
    this._setPageStatusLoadingByIndex($event.index, $event.loading);
    this.modeChangesIndex = $event.index;
    this.modeChanges = true;
    this.modeChangesMode = $event.mode;
    if (!$event.loading) {
      this.optimizedPagesLoading = true;
      this._store.dispatch(new fromStore.LoadBoosterData({websiteId: this.website?.id}));
    }
  }

  public getPageIdFromUrlAndRemove(pageId: number | string): void {
    this.optimizedPagesLoading = true;
    this._boosterService.removeOptimizedPage(this.website?.id, pageId)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(response => {
        this.pageUrl.setValue('');
        if (response.success) {
          this._store.dispatch(new fromStore.LoadBoosterData({websiteId: this.website?.id}));
          setTimeout(() => {
            NotifierComponent.notify(new Notification(TextUtils.getText('text_page_deleted'), NotificationType.SUCCESS));
            this.optimizedPagesLoading = false;
          }, 5000);
        }
      });
  }

  public removePage(pageId: number | string): void {
    const modal = this._dbModalService.open(RemoveBoostedPageDialogComponent, {height: 234, width: 500});
    modal.componentInstance.paidClient = this.currentSubscription.paidClient;
    modal.closed.subscribe(() => {
      this._setPageStatusLoading(pageId, true);
      this.getPageIdFromUrlAndRemove(pageId);
    });
  }

  public validatePageUrl(goToUpgradePage: boolean): void {
    if (goToUpgradePage) {
      this._router.navigate(['/upgrade-plan']);
      return;
    }
    if (this.pageUrl.invalid) return;
    this.loading = true;
    this._boosterService.getPageId(this.website?.id, this.pageUrl.value)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(response => {
        if (response.success && response.data && response.data.page_id) {
          this._optimizePage(response.data.page_id);
        } else {
          NotifierComponent.notify(new Notification(TextUtils.getText('text_somethingWentWrongDot'), NotificationType.DANGER));
          this.loading = false;
          this.serverError = 'URL doesn’t exist';
          this._cdRef.markForCheck();
        }
      });

  }

  private _optimizePage(pageId: number | 'front_page'): void {
    this.optimizedPages.push(this._createOptimizedPage(pageId));
    this._cdRef.markForCheck();
    this.optimizedPagesLoading = true;
    this.addedNewPage = true;
    this._boosterService.applyBoosterMode(this.website?.id, pageId, this.boosterMode, 1)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(response => {
        if (!response.success) {
          this.serverError = 'Optimization failed';
        } else {
          setTimeout(() => {
            NotifierComponent.notify(new Notification(TextUtils.getText('text_page_added'), NotificationType.SUCCESS));
            /*TODO SONA check*/
            this.optimizedPagesLoading = false;
          }, 3000);
        }
        this.pageUrl.setValue(' ');
        this.loading = false;
        this._store.dispatch(new fromStore.LoadBoosterData({websiteId: this.website?.id}));
      });
  }

  private _setValidators(): void {
    const regexp = new RegExp(Utils.replaceUrl(this.website?.site_url) + '+');
    const validators = [Validators.required, Validators.pattern(regexp), DbValidators.trim];
    this.pageUrl.setValidators(validators);
  }

  private _checkBoosterPluginUpdateAvailable(): void {
    this._store.select(fromStore.getBoosterPluginInfo(this.website?.id))
      .pipe(
        filter(pluginInfo => !!pluginInfo)
      )
      .subscribe(([pluginUpdateAvailable, currentVersion]) => {
        if (((!TypeUtils.isNull(pluginUpdateAvailable) && !!pluginUpdateAvailable) || (TypeUtils.isNull(pluginUpdateAvailable) && TypeUtils.isNull(currentVersion)))) {
          this.boosterUpdateAvailable = true;
        }
      });
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  public redirectToGPSI(url: string): void {
    window.open(`https://developers.google.com/speed/pagespeed/insights/?url=${url}`, '_blank', "noopener");
  }

  private _setPageStatusLoading(pageId: number | string, loading: boolean): void {
    this.optimizedPages.map(page => {
      if (page.id === pageId) {
        page.status = loading ? 'loading' : 'done';
      }
    });
    this._cdRef.markForCheck();
  }

  private _setPageStatusLoadingByIndex(index: number, loading: boolean): void {
    this.optimizedPages[index].status = loading ? 'loading' : 'done';
    this._cdRef.markForCheck();
  }

  private _createOptimizedPage(pageId: number | 'front_page'): BoosterPage {
    return BoosterPageMapper.map({
      date: '',
      is_custom: false,
      mode: this.boosterMode,
      page_id: pageId,
      status: 'loading',
      title: false,
      url: this.pageUrl.value,
    });
  }

    private _getCompanyInfo(): void {
        this._builderSectionsInteractionService.companyInfo
            .pipe(
                filter(companyInfo => !!companyInfo),
                takeUntil(this._destroyStream$),
                first()
            )
            .subscribe(companyInfo => {
                this.companyName = companyInfo?.branding?.company_name || companyInfo?.agency?.name || 'YourBrand';
            });
    }

}
